import React from 'react';
import { Box, Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

import { translate as t } from 'hooks/useTranslations';
import { neutral, error } from '_lex/_foundations/phxColors';
import LexCard from '_lex/_atoms/LexCard';
import LexTag from '_lex/_atoms/LexTag';
import PhxBadge from '_lex/_atoms/PhxBadge';
import ActionMenu from 'components/_atoms/ActionMenu';
import type { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import datetimeHelper from 'helpers/datetimeHelper';
import type { DraftManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import draftManualHelper from 'views/App/Portal/Policy/DraftManuals/helpers/draftManualHelper';

export type DraftManualCardProps = {
  manual: DraftManualItem;
  verticalName: string;
  actionMenuItems: ActionMenuItemProps[];
  onPublish: (manual: DraftManualItem) => void;
  onExport: (manual: DraftManualItem) => void;
  canPublish: boolean;
  canExport: boolean;
  onManualClick?: (manual: DraftManualItem) => void;
};

const DraftManualCard: React.FC<DraftManualCardProps> = ({
  manual,
  verticalName,
  actionMenuItems,
  onPublish,
  onExport,
  canPublish,
  canExport,
  onManualClick,
}) => {
  const approvedCount = draftManualHelper.getApprovedCount(manual);
  const isPublishDisabled = approvedCount === 0;

  const manualWithPublishedAt = manual as DraftManualItem & { publishedAt?: string };
  const publishedDate = manualWithPublishedAt.publishedAt
    ? datetimeHelper.formatDate(manualWithPublishedAt.publishedAt)
    : '\u2014';

  const publishButton = (
    <Button
      variant='text'
      size='small'
      disabled={isPublishDisabled}
      endIcon={<ArrowForwardIcon />}
      onClick={(e) => { e.stopPropagation(); if (!isPublishDisabled) onPublish(manual); }}
    >
      {t('draftManuals.actions.publish')}
    </Button>
  );

  const header = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <LexTag
        variant='default'
        label={manual.manualType}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {canExport && (
          <Tooltip title={t('draftManuals.export.downloadTooltip', 'Export this manual or table of contents')}>
            <IconButton
              onClick={(e) => { e.stopPropagation(); onExport(manual); }}
              aria-label={t('draftManuals.export.downloadTooltip', 'Export this manual or table of contents')}
            >
              <SaveAltIcon />
            </IconButton>
          </Tooltip>
        )}
        {actionMenuItems.length > 0 && (
          <Box component='span' onClick={(e) => e.stopPropagation()}>
            <ActionMenu
              id={`card_action_menu_${manual.id}`}
              items={actionMenuItems}
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  const footer = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      {approvedCount > 0 && (
        <PhxBadge color='success' size='sm'>
          {t('policy.manuals.properties.approvals', 'Approvals')}: {approvedCount} {t('policy.manuals.properties.policies', 'Policies')}
        </PhxBadge>
      )}
      {canPublish && (
        <Box sx={{ ml: 'auto' }}>
          {isPublishDisabled ? (
            <Tooltip title={t('draftManuals.actions.noApprovedPoliciesToPublish')}>
              <span>{publishButton}</span>
            </Tooltip>
          ) : (
            publishButton
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
    <LexCard
      header={header}
      footer={footer}
      sx={{
        transition: 'background-color 0.15s ease',
      }}
    >
      <Stack
        spacing={1}
        sx={{ px: 2, py: 1.5, pb: 3 }}
      >
        {onManualClick ? (
          <Tooltip
            title={t('draftManuals.card.openTooltip', 'Click to open this manual')}
            placement='top-start'
          >
            <Box component='span' sx={{ display: 'inline-flex', maxWidth: '80%' }}>
              <Link
                component='button'
                onClick={(e) => { e.stopPropagation(); onManualClick(manual); }}
                sx={{
                  textAlign: 'left',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'underline',
                  maxWidth: '100%',
                }}
              >
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 'semibold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {manual.name}
                </Typography>
                <ArrowOutwardIcon sx={{ fontSize: 16, flexShrink: 0 }} />
              </Link>
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant='h6'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '80%',
            }}
          >
            {manual.name}
          </Typography>
        )}
        <Typography variant='body2' sx={{ color: verticalName ? neutral[700] : error[500] }}>
          {verticalName || t('policy.manuals.properties.noVertical', 'No vertical')}
        </Typography>
        <Typography variant='body2' sx={{ color: neutral[500] }}>
          {t('policy.manuals.properties.lastPublishedAt', 'Published:')} {publishedDate}
        </Typography>
      </Stack>
    </LexCard>
    </Box>
  );
};

export default DraftManualCard;
